/**
 * Color utility class for color parsing, manipulation, and validation.
 *
 * This class is immutable - all manipulation methods return new Color instances.
 */

export class Color {
  // Internal storage: RGB (0-255), Alpha (0-1)
  private readonly _r: number;
  private readonly _g: number;
  private readonly _b: number;
  private readonly _a: number;

  private constructor(r: number, g: number, b: number, a = 1) {
    this._r = Math.round(Math.max(0, Math.min(255, r)));
    this._g = Math.round(Math.max(0, Math.min(255, g)));
    this._b = Math.round(Math.max(0, Math.min(255, b)));
    this._a = Math.max(0, Math.min(1, a));
  }

  // ================================
  // Factory Methods
  // ================================

  static fromRGB(rgb: { r: number; g: number; b: number; a?: number }): Color {
    return new Color(rgb.r, rgb.g, rgb.b, rgb.a ?? 1);
  }

  static fromHSL(hsl: { h: number; s: number; l: number; a?: number }): Color {
    const { r, g, b } = Color.hslToRgb(hsl.h, hsl.s, hsl.l);
    return new Color(r, g, b, hsl.a ?? 1);
  }

  /**
   * Parse a color string. Supports:
   * - Hex: #RGB, #RRGGBB, #RRGGBBAA
   * - RGB: rgb(r, g, b), rgba(r, g, b, a)
   * - HSL: hsl(h, s%, l%), hsla(h, s%, l%, a)
   * - OKLCH: oklch(L C H), oklch(L C H / a)
   *
   * @throws Error if the string cannot be parsed
   */
  static parse(str: string): Color {
    const trimmed = str.trim().toLowerCase();

    // Try hex format
    if (trimmed.startsWith("#")) {
      return Color.parseHex(trimmed);
    }

    // Try rgb/rgba format
    if (trimmed.startsWith("rgb")) {
      return Color.parseRGB(trimmed);
    }

    // Try hsl/hsla format
    if (trimmed.startsWith("hsl")) {
      return Color.parseHSL(trimmed);
    }

    // Try oklch format
    if (trimmed.startsWith("oklch")) {
      return Color.parseOKLCH(trimmed);
    }

    throw new Error(`Cannot parse color string: ${str}`);
  }

  /**
   * Try to parse a color string, returning null if parsing fails.
   */
  static tryParse(str: string): Color | null {
    try {
      return Color.parse(str);
    } catch {
      return null;
    }
  }

  // ================================
  // Parsing Helpers
  // ================================

  private static parseHex(hex: string): Color {
    let h = hex.replace("#", "");

    // Expand shorthand (#RGB -> #RRGGBB)
    if (h.length === 3) {
      h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
    } else if (h.length === 4) {
      h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2] + h[3] + h[3];
    }

    if (h.length !== 6 && h.length !== 8) {
      throw new Error(`Invalid hex color: ${hex}`);
    }

    const r = parseInt(h.substring(0, 2), 16);
    const g = parseInt(h.substring(2, 4), 16);
    const b = parseInt(h.substring(4, 6), 16);
    const a = h.length === 8 ? parseInt(h.substring(6, 8), 16) / 255 : 1;

    if (isNaN(r) || isNaN(g) || isNaN(b) || isNaN(a)) {
      throw new Error(`Invalid hex color: ${hex}`);
    }

    return new Color(r, g, b, a);
  }

  private static parseRGB(str: string): Color {
    // Match rgb(r, g, b) or rgba(r, g, b, a) - supports spaces and decimals
    const match = str.match(
      /rgba?\(\s*(\d+(?:\.\d+)?)\s*,\s*(\d+(?:\.\d+)?)\s*,\s*(\d+(?:\.\d+)?)\s*(?:,\s*(\d+(?:\.\d+)?)\s*)?\)/
    );

    if (!match) {
      throw new Error(`Invalid rgb color: ${str}`);
    }

    const r = parseFloat(match[1]);
    const g = parseFloat(match[2]);
    const b = parseFloat(match[3]);
    const a = match[4] !== undefined ? parseFloat(match[4]) : 1;

    return new Color(r, g, b, a);
  }

  private static parseHSL(str: string): Color {
    // Match hsl(h, s%, l%) or hsla(h, s%, l%, a)
    const match = str.match(
      /hsla?\(\s*(\d+(?:\.\d+)?)\s*,\s*(\d+(?:\.\d+)?)%\s*,\s*(\d+(?:\.\d+)?)%\s*(?:,\s*(\d+(?:\.\d+)?)\s*)?\)/
    );

    if (!match) {
      throw new Error(`Invalid hsl color: ${str}`);
    }

    const h = parseFloat(match[1]) / 360; // Convert degrees to 0-1
    const s = parseFloat(match[2]) / 100; // Convert percentage to 0-1
    const l = parseFloat(match[3]) / 100; // Convert percentage to 0-1
    const a = match[4] !== undefined ? parseFloat(match[4]) : 1;

    return Color.fromHSL({ h, s, l, a });
  }

  private static parseOKLCH(str: string): Color {
    // Match oklch(L C H) or oklch(L C H / a) - CSS Color Level 4 syntax
    // L can be 0-1 or 0%-100%, C is typically 0-0.4, H is 0-360
    const match = str.match(
      /oklch\(\s*(\d+(?:\.\d+)?%?)\s+(\d+(?:\.\d+)?)\s+(\d+(?:\.\d+)?)\s*(?:\/\s*(\d+(?:\.\d+)?%?)\s*)?\)/
    );

    if (!match) {
      throw new Error(`Invalid oklch color: ${str}`);
    }

    // Parse L (can be percentage or decimal)
    let l = parseFloat(match[1]);
    if (match[1].endsWith("%")) {
      l = l / 100;
    }

    const c = parseFloat(match[2]);
    const h = parseFloat(match[3]);

    // Parse alpha (can be percentage or decimal)
    let a = 1;
    if (match[4] !== undefined) {
      a = parseFloat(match[4]);
      if (match[4].endsWith("%")) {
        a = a / 100;
      }
    }

    const rgb = Color.oklchToRgb(l, c, h);
    return new Color(rgb.r, rgb.g, rgb.b, a);
  }

  // ================================
  // Color Space Conversion
  // ================================

  private static hslToRgb(h: number, s: number, l: number): { r: number; g: number; b: number } {
    let r: number, g: number, b: number;

    if (s === 0) {
      r = g = b = l;
    } else {
      const hue2rgb = (p: number, q: number, t: number): number => {
        if (t < 0) {
          t += 1;
        }
        if (t > 1) {
          t -= 1;
        }
        if (t < 1 / 6) {
          return p + (q - p) * 6 * t;
        }
        if (t < 1 / 2) {
          return q;
        }
        if (t < 2 / 3) {
          return p + (q - p) * (2 / 3 - t) * 6;
        }
        return p;
      };

      const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
      const p = 2 * l - q;
      r = hue2rgb(p, q, h + 1 / 3);
      g = hue2rgb(p, q, h);
      b = hue2rgb(p, q, h - 1 / 3);
    }

    return {
      r: Math.round(r * 255),
      g: Math.round(g * 255),
      b: Math.round(b * 255),
    };
  }

  private static rgbToHsl(r: number, g: number, b: number): { h: number; s: number; l: number } {
    const rNorm = r / 255;
    const gNorm = g / 255;
    const bNorm = b / 255;

    const max = Math.max(rNorm, gNorm, bNorm);
    const min = Math.min(rNorm, gNorm, bNorm);
    const l = (max + min) / 2;

    let h = 0;
    let s = 0;

    if (max !== min) {
      const d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

      switch (max) {
        case rNorm:
          h = ((gNorm - bNorm) / d + (gNorm < bNorm ? 6 : 0)) / 6;
          break;
        case gNorm:
          h = ((bNorm - rNorm) / d + 2) / 6;
          break;
        case bNorm:
          h = ((rNorm - gNorm) / d + 4) / 6;
          break;
      }
    }

    return { h, s, l };
  }

  // ================================
  // OKLCH Color Space Conversion
  // ================================

  // Convert sRGB (0-255) to linear RGB (0-1)
  private static srgbToLinear(c: number): number {
    const s = c / 255;
    return s <= 0.04045 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  }

  // Convert linear RGB (0-1) to sRGB (0-255)
  private static linearToSrgb(c: number): number {
    const s = c <= 0.0031308 ? 12.92 * c : 1.055 * Math.pow(c, 1 / 2.4) - 0.055;
    return Math.round(Math.max(0, Math.min(255, s * 255)));
  }

  // Convert linear RGB to OKLab
  private static linearRgbToOklab(
    r: number,
    g: number,
    b: number
  ): { L: number; a: number; b: number } {
    // Linear RGB to LMS
    const l = 0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b;
    const m = 0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b;
    const s = 0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b;

    // LMS to LMS' (cube root)
    const l_ = Math.cbrt(l);
    const m_ = Math.cbrt(m);
    const s_ = Math.cbrt(s);

    // LMS' to OKLab
    return {
      L: 0.2104542553 * l_ + 0.793617785 * m_ - 0.0040720468 * s_,
      a: 1.9779984951 * l_ - 2.428592205 * m_ + 0.4505937099 * s_,
      b: 0.0259040371 * l_ + 0.7827717662 * m_ - 0.808675766 * s_,
    };
  }

  // Convert OKLab to linear RGB
  private static oklabToLinearRgb(
    L: number,
    a: number,
    b: number
  ): { r: number; g: number; b: number } {
    // OKLab to LMS'
    const l_ = L + 0.3963377774 * a + 0.2158037573 * b;
    const m_ = L - 0.1055613458 * a - 0.0638541728 * b;
    const s_ = L - 0.0894841775 * a - 1.291485548 * b;

    // LMS' to LMS (cube)
    const l = l_ * l_ * l_;
    const m = m_ * m_ * m_;
    const s = s_ * s_ * s_;

    // LMS to linear RGB
    return {
      r: 4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s,
      g: -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s,
      b: -0.0041960863 * l - 0.7034186147 * m + 1.707614701 * s,
    };
  }

  // Convert RGB (0-255) to OKLCH
  private static rgbToOklch(r: number, g: number, b: number): { l: number; c: number; h: number } {
    // sRGB to linear RGB
    const linR = Color.srgbToLinear(r);
    const linG = Color.srgbToLinear(g);
    const linB = Color.srgbToLinear(b);

    // Linear RGB to OKLab
    const lab = Color.linearRgbToOklab(linR, linG, linB);

    // OKLab to OKLCH
    const c = Math.sqrt(lab.a * lab.a + lab.b * lab.b);
    let h = Math.atan2(lab.b, lab.a) * (180 / Math.PI);
    if (h < 0) {
      h += 360;
    }

    return { l: lab.L, c, h };
  }

  // Convert OKLCH to RGB (0-255)
  private static oklchToRgb(l: number, c: number, h: number): { r: number; g: number; b: number } {
    // OKLCH to OKLab
    const hRad = h * (Math.PI / 180);
    const a = c * Math.cos(hRad);
    const b = c * Math.sin(hRad);

    // OKLab to linear RGB
    const linRgb = Color.oklabToLinearRgb(l, a, b);

    // Linear RGB to sRGB
    return {
      r: Color.linearToSrgb(linRgb.r),
      g: Color.linearToSrgb(linRgb.g),
      b: Color.linearToSrgb(linRgb.b),
    };
  }

  // ================================
  // Getters
  // ================================

  get r(): number {
    return this._r;
  }

  get g(): number {
    return this._g;
  }

  get b(): number {
    return this._b;
  }

  get alpha(): number {
    return this._a;
  }

  get hsl(): { h: number; s: number; l: number } {
    return Color.rgbToHsl(this._r, this._g, this._b);
  }

  get oklch(): { l: number; c: number; h: number } {
    return Color.rgbToOklch(this._r, this._g, this._b);
  }

  // ================================
  // Manipulation Methods (immutable)
  // ================================

  /**
   * Adjust lightness using OKLCH color space (perceptually uniform).
   * @param multiplier Factor to multiply lightness (e.g., 1.2 = 20% lighter, 0.8 = 20% darker)
   */
  lighten(multiplier: number): Color {
    const { l, c, h } = this.oklch;
    const newL = Math.max(0, Math.min(1, l * multiplier));
    const rgb = Color.oklchToRgb(newL, c, h);
    return new Color(rgb.r, rgb.g, rgb.b, this._a);
  }

  /**
   * Adjust chroma (saturation) using OKLCH color space (perceptually uniform).
   * @param multiplier Factor to multiply chroma (e.g., 1.2 = 20% more saturated, 0.8 = 20% less)
   */
  saturate(multiplier: number): Color {
    const { l, c, h } = this.oklch;
    const newC = Math.max(0, c * multiplier);
    const rgb = Color.oklchToRgb(l, newC, h);
    return new Color(rgb.r, rgb.g, rgb.b, this._a);
  }

  withAlpha(alpha: number): Color {
    return new Color(this._r, this._g, this._b, alpha);
  }

  /**
   * Flatten a translucent color onto an opaque `backdrop` (standard source-over compositing),
   * returning the opaque color a viewer actually sees.
   *
   * Needed because `relativeLuminance()` — and therefore `contrastRatio()`, `readableText()` and
   * `ensureContrast()` — read only the RGB channels and ignore alpha. Measuring `rgba(239,68,68,0.12)`
   * directly scores it as solid red (dark) when on a white card it paints as a very light pink, which
   * flips every downstream contrast decision. Composite first, then measure.
   */
  compositeOver(backdrop: Color): Color {
    if (this._a >= 1) {
      return this;
    }
    const blend = (fg: number, bg: number) => Math.round(fg * this._a + bg * (1 - this._a));
    // The backdrop is treated as opaque: it is the surface being painted on, and a partly
    // transparent one would need the whole stack below it to resolve to a real color.
    return new Color(
      blend(this._r, backdrop._r),
      blend(this._g, backdrop._g),
      blend(this._b, backdrop._b),
      1
    );
  }

  // ================================
  // Serialization Methods
  // ================================

  toHexString(): string {
    const toHex = (n: number) => n.toString(16).padStart(2, "0");
    return `#${toHex(this._r)}${toHex(this._g)}${toHex(this._b)}`;
  }

  toRgbString(): string {
    if (this._a < 1) {
      return `rgba(${this._r}, ${this._g}, ${this._b}, ${this._a})`;
    }
    return `rgb(${this._r}, ${this._g}, ${this._b})`;
  }

  toHslString(): string {
    const { h, s, l } = this.hsl;
    const hDeg = Math.round(h * 360);
    const sPct = Math.round(s * 100);
    const lPct = Math.round(l * 100);

    if (this._a < 1) {
      return `hsla(${hDeg}, ${sPct}%, ${lPct}%, ${this._a})`;
    }
    return `hsl(${hDeg}, ${sPct}%, ${lPct}%)`;
  }

  toOklchString(): string {
    const { l, c, h } = this.oklch;
    // Round to reasonable precision
    const lRound = Math.round(l * 1000) / 1000;
    const cRound = Math.round(c * 1000) / 1000;
    const hRound = Math.round(h * 10) / 10;

    if (this._a < 1) {
      return `oklch(${lRound} ${cRound} ${hRound} / ${this._a})`;
    }
    return `oklch(${lRound} ${cRound} ${hRound})`;
  }

  // ================================
  // WCAG Contrast Utilities
  // ================================

  /**
   * Calculate the relative luminance of this color.
   * Based on WCAG 2.1 formula: https://www.w3.org/TR/WCAG21/#dfn-relative-luminance
   * @returns Luminance value between 0 (darkest) and 1 (lightest)
   */
  relativeLuminance(): number {
    const sRGB = (c: number) => {
      const s = c / 255;
      return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
    };

    const R = sRGB(this._r);
    const G = sRGB(this._g);
    const B = sRGB(this._b);

    return 0.2126 * R + 0.7152 * G + 0.0722 * B;
  }

  /**
   * Calculate the contrast ratio between this color and another.
   * Based on WCAG 2.1 formula: https://www.w3.org/TR/WCAG21/#dfn-contrast-ratio
   * @param other The other color to compare against
   * @returns Contrast ratio between 1 (no contrast) and 21 (max contrast, black vs white)
   */
  contrastRatio(other: Color): number {
    const L1 = this.relativeLuminance();
    const L2 = other.relativeLuminance();

    const lighter = Math.max(L1, L2);
    const darker = Math.min(L1, L2);

    return (lighter + 0.05) / (darker + 0.05);
  }

  /**
   * Check if this color meets WCAG contrast requirements against another color.
   * @param other The other color to compare against
   * @param level 'AA' (4.5:1 for normal text, 3:1 for large text) or 'AAA' (7:1 for normal text, 4.5:1 for large text)
   * @param largeText Whether the text is large (14pt bold or 18pt regular)
   * @returns true if the contrast meets the specified WCAG level
   */
  meetsWCAG(other: Color, level: "AA" | "AAA" = "AA", largeText = false): boolean {
    const ratio = this.contrastRatio(other);

    if (level === "AAA") {
      return largeText ? ratio >= 4.5 : ratio >= 7;
    }
    // AA level
    return largeText ? ratio >= 3 : ratio >= 4.5;
  }

  /**
   * Determine if this color is considered "light" (useful for choosing text color).
   * Uses the relative luminance threshold of 0.179 (based on WCAG recommendations).
   */
  isLight(): boolean {
    return this.relativeLuminance() > 0.179;
  }

  /**
   * Determine if this color is considered "dark".
   */
  isDark(): boolean {
    return !this.isLight();
  }
}

const WHITE = Color.parse("#ffffff");
const NEAR_BLACK = Color.parse("#111827");

/** Pick the more readable of white / near-black text for a given background. */
export function readableText(bg: Color): Color {
  return bg.contrastRatio(WHITE) >= bg.contrastRatio(NEAR_BLACK) ? WHITE : NEAR_BLACK;
}

/**
 * Nudge a foreground color's lightness toward white or black until it meets a WCAG contrast target
 * against `bg` (or we run out of headroom). Preserves hue/chroma so the color stays on-brand — a
 * status red stays red instead of collapsing to monochrome text.
 */
export function ensureContrast(fg: Color, bg: Color, target = 4.5): Color {
  const goLighter = bg.relativeLuminance() < 0.5;
  let current = fg;
  for (let i = 0; i < 12 && current.contrastRatio(bg) < target; i++) {
    current = current.lighten(goLighter ? 1.08 : 0.92);
  }
  // Fall back to guaranteed-readable text if the color simply can't hit the target.
  return current.contrastRatio(bg) >= target ? current : readableText(bg);
}
